import { useQuery } from '@tanstack/react-query';
import { AlertTriangleIcon, ChevronRightIcon } from 'lucide-react';
import { type JSX, useState } from 'react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import { artifactSharesQueryOptions } from '@/lib/api/artifact-shares.js';
import { sharedLinksLabel } from '@/lib/share-reach.js';
import { cn } from '@/lib/utils.js';

import { ArtifactShareList } from './ArtifactShareList.js';
import {
  notDeliveredLabel,
  summarizeArtifactShares,
} from './artifact-share-summary.js';

/**
 * This file's own shares, folded behind one row.
 *
 * Everything else on this dialog is a fixed set of controls; the shares are the one part that grows for
 * as long as somebody keeps sharing, and left open they were two thirds of the whole screen. So the list
 * is a disclosure, and the closed row carries what the list would otherwise be the only place to read.
 *
 * Two things it must not hide. The count is of shares that STILL OPEN, because that is the set the list
 * draws — counting revoked ones would make the row and the list disagree, and the row is the half a
 * person reads. And a failed notification is marked on the closed row: it is the author's only sign that
 * nobody was told, and a fact that only exists behind a fold is a fact nobody has.
 *
 * With nothing shared out there is no row at all — the list's own one-line "not shared yet" is the whole
 * answer, and a disclosure that opens onto nothing is a promise of rows that are not coming.
 */
export function ArtifactSharesDisclosure({
  workspaceId,
  artifactId,
}: {
  workspaceId: string;
  artifactId: string;
}): JSX.Element {
  const sharesQuery = useQuery(
    artifactSharesQueryOptions(workspaceId, artifactId),
  );
  const [open, setOpen] = useState(false);
  const summary = summarizeArtifactShares(sharesQuery.data ?? []);

  if (summary.liveCount === 0) {
    return (
      <div className="border-t pt-3">
        <ArtifactShareList workspaceId={workspaceId} artifactId={artifactId} />
      </div>
    );
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border-t pt-3"
      data-testid="artifact-shares-disclosure"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 text-left">
        <ChevronRightIcon
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden
        />
        <span className="text-sm">{sharedLinksLabel(summary.liveCount)}</span>
        {summary.notDeliveredCount > 0 && (
          <span
            data-testid="artifact-shares-not-delivered"
            className="ml-auto flex shrink-0 items-center gap-1 text-xs text-warning-600"
          >
            <AlertTriangleIcon className="size-3.5 shrink-0" aria-hidden />
            {notDeliveredLabel(summary.notDeliveredCount)}
          </span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1">
        <ArtifactShareList workspaceId={workspaceId} artifactId={artifactId} />
      </CollapsibleContent>
    </Collapsible>
  );
}
